import {
  ArrowDownIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  ArrowUpIcon,
} from "@heroicons/react/24/solid";
import { useOnboardingState, useOnboarding } from "hooks/useOnboarding";
import {
  CSSProperties,
  FC,
  MutableRefObject,
  ReactNode,
  useEffect,
  useState,
} from "react";
import {
  Props,
  Placement,
  STATUS,
  ACTIONS,
  CallBackProps,
  StoreHelpers,
  Step,
  LIFECYCLE,
} from "react-joyride";
import OnboardingTooltip from "./OnboardingTooltip";
import dynamic from "next/dynamic";
const ReactJoyride = dynamic(() => import("react-joyride"), { ssr: false });

interface OnboardingCTA {
  target: string;
  placement: Placement;
}

export interface OnboardingStep extends Step {
  nextButtonText?: string;
  hideSkipButton?: boolean;
}

interface OnboardingTourProps
  extends Pick<
    Props,
    "styles" | "tooltipComponent" | "spotlightClicks" | "stepIndex"
  > {
  id: string;
  delayRun?: number;
  tooltipStyle?: CSSProperties;
  nextButtonText?: string;
  showProgress?: boolean;
  steps: OnboardingStep[];
  progress?: ((index: number, size: number) => ReactNode) | ReactNode;
  forceRun?: boolean;
  helper?: MutableRefObject<StoreHelpers | null>;
  cta?: OnboardingCTA;
  ctaHelper?: MutableRefObject<StoreHelpers | null>;
  onFinish?: () => void;
  callback?: (data: CallBackProps) => void;
}

const OnboardingTour: FC<OnboardingTourProps> = ({
  id,
  steps,
  stepIndex,
  styles,
  tooltipComponent,
  forceRun = false,
  onFinish = () => {},
  helper,
  cta,
  delayRun,
  ctaHelper,
  tooltipStyle,
  nextButtonText,
  showProgress,
  progress,
  spotlightClicks,
  callback,
}) => {
  const { shouldRun } = useOnboardingState(id);
  const { removeShouldRun } = useOnboarding();
  const [runCTA, setRunCTA] = useState(false);
  const [show, setShow] = useState(forceRun || !delayRun);

  useEffect(() => {
    if (delayRun && !forceRun) {
      setTimeout(() => {
        setShow(true);
      }, delayRun);
    }
  }, [delayRun, forceRun]);

  return show ? (
    <>
      <ReactJoyride
        continuous={true}
        disableScrolling={true}
        disableOverlayClose={true}
        spotlightClicks={spotlightClicks}
        run={shouldRun || forceRun}
        stepIndex={stepIndex}
        getHelpers={(data) => {
          if (helper) {
            helper.current = data;
          }
        }}
        tooltipComponent={
          tooltipComponent
            ? tooltipComponent
            : (props) => (
                <OnboardingTooltip
                  {...props}
                  style={tooltipStyle}
                  showProgress={showProgress}
                  progress={progress}
                  nextButtonText={nextButtonText}
                />
              )
        }
        styles={styles}
        steps={steps}
        callback={(data) => {
          const { status, action, lifecycle } = data;

          if (status === STATUS.FINISHED) {
            removeShouldRun(id);
            if (cta) {
              setRunCTA(true);
            } else {
              onFinish();
            }
          }

          if (action === ACTIONS.SKIP) {
            removeShouldRun(id);
            onFinish();
          }

          callback && callback(data);
        }}
      />
      {cta && (
        <ReactJoyride
          continuous={true}
          disableOverlayClose={true}
          disableScrolling={true}
          run={runCTA}
          getHelpers={(helper) => {
            if (ctaHelper) {
              ctaHelper.current = helper;
            }
          }}
          spotlightClicks={true}
          tooltipComponent={({ tooltipProps }) => {
            switch (cta.placement) {
              case "top":
              case "top-start":
              case "top-end":
                return (
                  <div {...tooltipProps}>
                    <ArrowDownIcon className="h-10 w-10 animate-bounce text-white" />
                  </div>
                );
              case "bottom":
              case "bottom-start":
              case "bottom-end":
                return (
                  <div {...tooltipProps}>
                    <ArrowUpIcon className="animate-bounce-up h-10 w-10 text-white" />
                  </div>
                );
              case "left":
              case "left-start":
              case "left-end":
                return (
                  <div {...tooltipProps}>
                    <ArrowRightIcon className="animate-bounce-right h-10 w-10 text-white" />
                  </div>
                );
              case "right":
              case "right-start":
              case "right-end":
                return (
                  <div {...tooltipProps}>
                    <ArrowLeftIcon className="animate-bounce-left h-10 w-10 text-white" />
                  </div>
                );
            }
          }}
          styles={{
            options: {
              arrowColor: "transparent",
            },
          }}
          steps={[
            {
              target: cta.target,
              placement: cta.placement,
              title: "",
              content: "",
              disableBeacon: true,
              hideFooter: true,
              disableScrolling: true,
            },
          ]}
          callback={({ status }) => {
            if (status === STATUS.FINISHED) {
              setRunCTA(false);
              onFinish();
            }
          }}
        />
      )}
    </>
  ) : (
    <></>
  );
};

export default OnboardingTour;
